import React from "react";
import styles from "./IllustratedTextList.module.scss";
import { InformationMedia } from "../information/information.component";

interface IllustratedText {
  image: string;
  kicker?: string;
  title: string;
  text: React.FC;
  // Per-row tuning for its specific copy volume:
  // wideArt — rows whose beside-copy runs short hand more width to the art.
  wideArt?: boolean;
  // besideBlocks — how many leading copy blocks (the quote counts as one)
  // share the row with the floated panel. Every later block CLEARS below it,
  // so a paragraph can never open in the one-line sliver beside the panel's
  // tail — the strand that shows up at unlucky viewport widths. Pick it so
  // the beside-copy roughly fills the panel's height.
  besideBlocks: 1 | 2 | 3;
}

interface IllustratedTextListProps {
  items: IllustratedText[];
}

// Domain sections in the site's card language: a title plate over a techno
// card carrying the holo-framed illustration as a floated panel with its
// chassis caption. Copy wraps beside the panel only for the blocks each row
// declares safe (besideBlocks); the rest clears below it. The row's ghost
// numeral sits in the card's bottom outer corner — the catalogue detail that
// makes each entry its own instrument page.
export const IllustratedTextListComponent: React.FC<
  IllustratedTextListProps
> = ({ items }) => {
  return (
    <div className={styles["illustrated-text-list"]}>
      {items.map((item: IllustratedText, index: number) => {
        const numeral = String(index + 1).padStart(2, "0");
        const rowClasses = [
          styles["row"],
          styles[`row-beside-${item.besideBlocks}`],
          index % 2 === 1 ? styles["row-mirror"] : "",
          item.wideArt ? styles["row-wide-art"] : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <section key={item.title} className={rowClasses}>
            {item.kicker && (
              <span className={styles["kicker"]}>{item.kicker}</span>
            )}
            <h4>{item.title}</h4>
            <div className={styles["text-body"]} data-numeral={numeral}>
              <InformationMedia
                image={item.image}
                alt={item.title}
                position={index % 2 === 0 ? "left" : "right"}
                caption={`Fig. ${numeral} / ${String(items.length).padStart(
                  2,
                  "0",
                )}`}
              />
              <item.text />
            </div>
          </section>
        );
      })}
    </div>
  );
};
